import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import type { FC } from 'react';
import { useMemo } from 'react';

import InfoTooltip from 'components/v2/common/infoTooltip';
import { AnalyticsEmpty } from '../../../components/AnalyticsState';
import { SectionTitle } from '../../../components/analyticsUi';

import type {
    AssessmentCohort,
    AssessmentDurationStats,
    AssessmentSectionTotal,
    AssessmentStageTimingByCohort,
} from 'hooks/analytics';
import useTranslate from 'hooks/useTranslate';
import formatDuration from '../../../utils/formatDuration';
import assessmentCompletionColors from '../assessmentCompletionColors';
import { compareAssessmentSections, getAssessmentStageLabel } from '../assessmentCompletionLabels';

interface StageTimingComparisonProps {
    stageTiming: AssessmentStageTimingByCohort;
    /** Provided only while one agent is selected; already summed across revisits. */
    selectedSectionTotals?: AssessmentSectionTotal[];
    agents: Array<{ agentId: number; agentName: string }>;
    selectedAgentId?: number;
    onAgentSelect: (agentId?: number) => void;
    selectedCohort: AssessmentCohort;
    onCohortSelect: (cohort: AssessmentCohort) => void;
    overallStats: AssessmentDurationStats;
}

interface TimelineRow {
    key: string;
    label: string;
    sampleSize: number;
    medianSeconds: number | null;
    startSeconds: number;
}

const cohortOptions: Array<{ value: AssessmentCohort; labelKey: string }> = [
    { value: 'combined', labelKey: 'assessmentAllAssessments' },
    { value: 'selfAssessment', labelKey: 'assessmentSelfAssessments' },
    { value: 'agentAssisted', labelKey: 'assessmentCohortAgentAssisted' },
    { value: 'withCv', labelKey: 'assessmentCvUploaded' },
    { value: 'withoutCv', labelKey: 'assessmentNoCvUploaded' },
];

/** A sample-less stage has no measurable duration, so it must not read as zero. */
const observedMedian = (stats: AssessmentDurationStats): number | null =>
    stats.sampleSize > 0 ? stats.medianSeconds : null;

const TimelineBar: FC<{
    startSeconds: number;
    seconds: number;
    maxSeconds: number;
    color: string;
    opacity?: number;
}> = ({ startSeconds, seconds, maxSeconds, color, opacity = 1 }) => (
    <Box sx={{ position: 'relative', height: 20, minWidth: 220 }}>
        <Box
            sx={{
                position: 'absolute',
                top: 9,
                right: 0,
                left: 0,
                borderTop: '2px solid',
                borderColor: 'action.hover',
            }}
        />
        <Box
            sx={{
                position: 'absolute',
                top: 4,
                left: `${(startSeconds / maxSeconds) * 100}%`,
                width: `${(Math.max(seconds, 0) / maxSeconds) * 100}%`,
                minWidth: 6,
                height: 12,
                bgcolor: color,
                borderRadius: 1,
                opacity,
            }}
        />
    </Box>
);

const StageTimingComparison: FC<StageTimingComparisonProps> = ({
    stageTiming,
    selectedSectionTotals,
    agents,
    selectedAgentId,
    onAgentSelect,
    selectedCohort,
    onCohortSelect,
    overallStats,
}) => {
    const { translate } = useTranslate();
    const isAgentSelected = selectedAgentId != null;

    const rows = useMemo<TimelineRow[]>(() => {
        const sections = isAgentSelected
            ? (selectedSectionTotals ?? []).map((total) => ({
                  key: total.section,
                  sampleSize: total.sampleSize,
                  medianSeconds: observedMedian(total),
              }))
            : (stageTiming[selectedCohort] ?? []).map((timing) => ({
                  key: timing.stage,
                  sampleSize: timing.sampleSize,
                  medianSeconds: observedMedian(timing.fromPreviousObserved),
              }));

        let elapsedSeconds = 0;
        return sections
            .sort((a, b) => compareAssessmentSections(a.key, b.key))
            .map((section) => {
                const startSeconds = elapsedSeconds;
                elapsedSeconds += Math.max(section.medianSeconds ?? 0, 0);
                return {
                    ...section,
                    label: getAssessmentStageLabel(section.key, translate),
                    startSeconds,
                };
            });
    }, [isAgentSelected, selectedCohort, selectedSectionTotals, stageTiming, translate]);

    const observedTotalSeconds = rows.reduce(
        (total, row) => total + Math.max(row.medianSeconds ?? 0, 0),
        0
    );
    const timelineMaxSeconds = Math.max(observedTotalSeconds, 1);
    const timelineColor = isAgentSelected
        ? assessmentCompletionColors.agentAssisted
        : assessmentCompletionColors[selectedCohort];
    const availableCohorts = cohortOptions.filter(
        ({ value }) => value === selectedCohort || stageTiming[value] != null
    );

    return (
        <Paper component="section" variant="outlined" sx={{ borderRadius: 2, p: 2, mb: 2 }}>
            <SectionTitle>
                {translate('assessmentStageTiming')}
                <InfoTooltip title={translate('assessmentStageTimingTooltip')} />
            </SectionTitle>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, mt: '-0.75rem' }}>
                {!isAgentSelected ? (
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                        <InputLabel id="assessment-stage-cohort-label">
                            {translate('assessmentCohort')}
                        </InputLabel>
                        <Select<AssessmentCohort>
                            id="assessment-stage-cohort"
                            labelId="assessment-stage-cohort-label"
                            value={selectedCohort}
                            label={translate('assessmentCohort')}
                            onChange={(event: SelectChangeEvent<AssessmentCohort>) =>
                                onCohortSelect(event.target.value as AssessmentCohort)
                            }
                        >
                            {availableCohorts.map(({ value, labelKey }) => (
                                <MenuItem key={value} value={value}>
                                    {translate(labelKey)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : null}
                <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel id="assessment-stage-agent-label">
                        {translate('assessmentSelectedAgent')}
                    </InputLabel>
                    <Select<number | ''>
                        id="assessment-stage-agent"
                        labelId="assessment-stage-agent-label"
                        value={selectedAgentId ?? ''}
                        label={translate('assessmentSelectedAgent')}
                        onChange={(event: SelectChangeEvent<number | ''>) =>
                            onAgentSelect(
                                event.target.value === '' ? undefined : Number(event.target.value)
                            )
                        }
                    >
                        <MenuItem value="">{translate('assessmentAllAgents')}</MenuItem>
                        {agents.map((agent) => (
                            <MenuItem key={agent.agentId} value={agent.agentId}>
                                {agent.agentName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {rows.length === 0 ? (
                <AnalyticsEmpty
                    title={translate('assessmentNoStageTiming')}
                    description={translate('assessmentNoStageTimingDescription')}
                />
            ) : (
                <TableContainer>
                    <Table size="small" sx={{ minWidth: 860 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('assessmentStage')}</TableCell>
                                <TableCell align="right">
                                    {translate('assessmentSampleSizeShort')}
                                </TableCell>
                                <TableCell align="right">
                                    {translate('assessmentMedianTime')}
                                </TableCell>
                                <TableCell>{translate('assessmentMedianTimeline')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.key} hover>
                                    <TableCell sx={{ fontWeight: 700 }}>{row.label}</TableCell>
                                    <TableCell align="right">{row.sampleSize}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                                        {row.medianSeconds == null
                                            ? translate('assessmentNoHistoricalTimingData')
                                            : formatDuration(row.medianSeconds)}
                                    </TableCell>
                                    <TableCell>
                                        {row.medianSeconds == null ? (
                                            <Typography
                                                color="text.secondary"
                                                sx={{ fontSize: 12, fontStyle: 'italic' }}
                                            >
                                                {translate('assessmentNoHistoricalTimingData')}
                                            </Typography>
                                        ) : (
                                            <TimelineBar
                                                startSeconds={row.startSeconds}
                                                seconds={row.medianSeconds}
                                                maxSeconds={timelineMaxSeconds}
                                                color={timelineColor}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell colSpan={2} sx={{ fontWeight: 800 }}>
                                    {translate('assessmentOverallMedianTime')}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800 }}>
                                    {overallStats.medianSeconds == null
                                        ? translate('assessmentNoDataValue')
                                        : formatDuration(overallStats.medianSeconds)}
                                </TableCell>
                                <TableCell>
                                    {observedTotalSeconds > 0 ? (
                                        <TimelineBar
                                            startSeconds={0}
                                            seconds={observedTotalSeconds}
                                            maxSeconds={timelineMaxSeconds}
                                            color={timelineColor}
                                            opacity={0.28}
                                        />
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default StageTimingComparison;
